'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';

interface NotFoundContentProps {
    title: string;
    description: string;
    backHomeLabel: string;
    goBackLabel: string;
    homeHref: string;
    isRTL?: boolean;
}

/**
 * EcoBoy 404 Page — Uses the platform's design system:
 * - Tailwind CSS with platform CSS variables (--primary, --background, etc.)
 * - shadcn/ui Button component
 * - Plus Jakarta Sans (LTR) / IBM Plex Sans Arabic (RTL) via font-sans
 */
export function NotFoundContent({
    title,
    description,
    backHomeLabel,
    goBackLabel,
    homeHref,
    isRTL = false,
}: NotFoundContentProps) {
    return (
        <div
            dir={isRTL ? 'rtl' : 'ltr'}
            className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background p-8 text-center font-sans antialiased"
        >
            <meta name="robots" content="noindex, nofollow" />

            {/* Background grid pattern */}
            <div
                className="pointer-events-none absolute inset-0 opacity-40 dark:opacity-20"
                style={{
                    backgroundImage: `
                        linear-gradient(hsl(var(--primary) / 0.07) 1px, transparent 1px),
                        linear-gradient(90deg, hsl(var(--primary) / 0.07) 1px, transparent 1px)
                    `,
                    backgroundSize: '60px 60px',
                }}
            />

            {/* Glow orb top-left */}
            <div
                className="pointer-events-none absolute top-[15%] left-[20%] h-[300px] w-[300px] rounded-full blur-[60px]"
                style={{
                    background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)',
                }}
            />
            {/* Glow orb bottom-right */}
            <div
                className="pointer-events-none absolute bottom-[10%] right-[15%] h-[250px] w-[250px] rounded-full blur-[60px]"
                style={{
                    background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 70%)',
                }}
            />

            {/* Giant 404 background text */}
            <div
                className="pointer-events-none absolute top-1/2 left-1/2 z-0 -translate-x-1/2 -translate-y-1/2 select-none text-[clamp(150px,25vw,350px)] font-black leading-none tracking-tighter"
                style={{ color: 'hsl(var(--primary) / 0.06)' }}
            >
                404
            </div>

            {/* Floating EcoBoy */}
            <div className="relative z-10 mb-2 animate-float">
                <img
                    src="/assets/ecoboy-404.png"
                    alt="EcoBoy"
                    width={200}
                    height={200}
                    className="h-auto w-[clamp(140px,18vw,200px)]"
                    style={{
                        filter: 'drop-shadow(0 20px 40px hsl(var(--primary) / 0.3))',
                    }}
                />
            </div>

            {/* 404 heading */}
            <h1
                className="relative z-10 m-0 text-[clamp(3.5rem,8vw,6rem)] font-black leading-none tracking-tighter"
                style={{
                    background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.7) 50%, hsl(var(--primary) / 0.5) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}
            >
                404
            </h1>

            {/* Title */}
            <h2 className="relative z-10 mt-3 mb-3 text-[clamp(1.2rem,2.5vw,1.6rem)] font-bold text-foreground">
                {title}
            </h2>

            {/* Description */}
            <p className="relative z-10 mb-8 max-w-md text-[clamp(0.875rem,1.3vw,1rem)] leading-relaxed text-muted-foreground">
                {description}
            </p>

            {/* CTA Buttons using shadcn/ui Button */}
            <div className="relative z-10 flex flex-wrap items-center justify-center gap-3">
                <Button asChild size="lg">
                    <Link href={homeHref}>
                        {backHomeLabel}
                    </Link>
                </Button>
                <Button
                    variant="outline"
                    size="lg"
                    onClick={() => typeof window !== 'undefined' && window.history.back()}
                >
                    {goBackLabel}
                </Button>
            </div>

            {/* Branding */}
            <p className="relative z-10 mt-12 text-xs font-medium tracking-wider text-muted-foreground/40">
                ecommaps.com
            </p>
        </div>
    );
}
